import CheckIcon from "@mui/icons-material/CheckRounded";
import { Alert, Box, Button, DialogContent, DialogTitle, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import CopyToBufferButton from "@entrilia/shared/components/inputs/CopyToBufferButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import UserWithAvatar from "@entrilia/shared/components/UserWithAvatar";
import WarningDialogIcon from "@entrilia/shared/icons/WarningDialogIcon";
import type { OrganizationContactRole, OrganizationMember } from "../../../../api/types/userManagementTypes";
import { pageRoutes } from "../../../../routes";
import { organizationContactRoleCaptionsMap } from "../../../../utilities/enumCaptions";

interface Props {
  onClose: () => void;
  memberToDeactivate: OrganizationMember;
  orgContactRoles: OrganizationContactRole[];
  membersToContact: OrganizationMember[];
}

const MemberDeactivationBlockedDialog = ({ onClose, memberToDeactivate, orgContactRoles, membersToContact }: Props) => {
  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Deactivate</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={3} sx={{ alignItems: "center" }}>
          <WarningDialogIcon sx={{ width: 80, height: 80 }} />

          <UserWithAvatar userName={memberToDeactivate.name} email={memberToDeactivate.email} />

          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">This member can&apos;t be deactivated yet</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              They&apos;re the only contact assigned to one or more required organization contact roles. A replacement
              will need to be assigned first.
            </Typography>
          </Stack>

          {orgContactRoles.length > 0 && (
            <Alert severity="warning" icon={false} sx={{ width: "100%" }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Sole contact for:</Typography>
                {orgContactRoles.map((role) => (
                  <Stack key={role} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <CheckIcon color="inherit" />
                    <Typography>{organizationContactRoleCaptionsMap[role]}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Alert>
          )}

          {membersToContact.length > 0 && (
            <Paper variant="outlined" sx={{ width: "100%", p: 1.5 }}>
              <Stack spacing={1}>
                <Typography color="textSecondary">
                  Reach out to your primary account contact to reassign these roles:
                </Typography>
                {membersToContact.map((member) => (
                  <Box key={member.userId} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <UserWithAvatar userName={member.name} email={member.email} />
                    <CopyToBufferButton textToCopy={member.email} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogButtons
        startAction={
          <Button
            component={RouterLink}
            color="primary"
            variant="outlined"
            to={`../${pageRoutes.settingsOrganizationContacts}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open organization contacts
          </Button>
        }
      >
        <Button color="primary" variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default MemberDeactivationBlockedDialog;
